import { z } from "zod";

export const businessIndustries = ["CLEANING", "LANDSCAPING"] as const;
export type BusinessIndustry = (typeof businessIndustries)[number];

export const websiteThemes = [
  "CLEAN_MODERN",
  "PREMIUM",
  "MINIMAL",
  "BOLD",
] as const;
export type WebsiteThemeId = (typeof websiteThemes)[number];

/** Approved layout families — AI picks among these; React renders them. */
export const websiteLayoutFamilies = [
  "CLASSIC",
  "EDITORIAL",
  "SPLIT_HERO",
  "MAGAZINE",
] as const;
export type WebsiteLayoutFamily = (typeof websiteLayoutFamilies)[number];

export const contentTones = [
  "friendly",
  "premium",
  "minimal",
  "bold",
  "professional",
] as const;
export type ContentTone = (typeof contentTones)[number];

export const websiteSectionKeys = [
  "header",
  "hero",
  "about",
  "services",
  "pricing",
  "serviceAreas",
  "gallery",
  "testimonials",
  "faq",
  "bookingCta",
  "contact",
  "footer",
] as const;
export type WebsiteSectionKey = (typeof websiteSectionKeys)[number];

export const servicePricingTypes = [
  "FIXED",
  "HOURLY",
  "STARTING_AT",
  "QUOTE_REQUIRED",
] as const;
export type ServicePricingType = (typeof servicePricingTypes)[number];

export const bookingStatuses = [
  "REQUESTED",
  "CONFIRMED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
  "REJECTED",
] as const;
export type BookingStatus = (typeof bookingStatuses)[number];

export const paymentCollectionModes = [
  "NONE",
  "DEPOSIT",
  "FULL_UPFRONT",
  "AFTER_SERVICE",
] as const;

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128)
  .regex(/[A-Za-z]/, "Password must include a letter")
  .regex(/[0-9]/, "Password must include a number");

const emailSchema = z
  .string()
  .trim()
  .email()
  .transform((value) => value.toLowerCase());

export const businessOwnerRegisterSchema = z
  .object({
    ownerName: z.string().trim().min(2).max(100),
    ownerEmail: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    businessName: z.string().trim().min(2).max(120),
    industry: z.enum(businessIndustries),
    country: z.string().length(2).default("US"),
    phoneNumber: z.string().trim().min(7).max(24),
    acceptTerms: z.literal(true),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

export const businessClientRegisterSchema = z
  .object({
    fullName: z.string().trim().min(2).max(100),
    email: emailSchema,
    phoneNumber: z.string().trim().min(7).max(24).optional(),
    password: passwordSchema,
    confirmPassword: z.string(),
    addressLine1: z.string().trim().min(5).max(150).optional(),
    city: z.string().trim().min(2).max(80).optional(),
    region: z.string().trim().min(2).max(80).optional(),
    postalCode: z.string().trim().min(3).max(20).optional(),
    country: z.string().length(2).default("US"),
    acceptTerms: z.literal(true),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

export const businessUpdateSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: z.string().max(5000).optional(),
  phoneNumber: z.string().trim().min(7).max(24).optional(),
  email: emailSchema.optional(),
  addressLine1: z.string().max(150).optional(),
  addressLine2: z.string().max(150).optional(),
  city: z.string().max(80).optional(),
  region: z.string().max(80).optional(),
  postalCode: z.string().max(20).optional(),
  country: z.string().length(2).optional(),
  timezone: z.string().min(2).max(64).optional(),
  locale: z.string().min(2).max(16).optional(),
  currencyCode: z.string().length(3).optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  brandTone: z.string().max(120).optional(),
  heroHeadline: z.string().max(200).optional(),
  callToAction: z.string().max(120).optional(),
  aboutContent: z.string().max(8000).optional(),
  businessHours: z.record(z.string(), z.unknown()).optional(),
  paymentCollectionMode: z.enum(paymentCollectionModes).optional(),
});

export const serviceCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().max(2000).optional(),
  pricingType: z.enum(servicePricingTypes).default("FIXED"),
  priceCents: z.number().int().min(0).max(10_000_000).optional(),
  durationMinutes: z
    .number()
    .int()
    .min(15)
    .max(24 * 60)
    .optional(),
  imageUrl: z.string().url().max(2048).optional(),
  isBookableOnline: z.boolean().default(true),
});

export const serviceUpdateSchema = serviceCreateSchema.partial().extend({
  isArchived: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});

export const bookingCreateSchema = z.object({
  serviceId: z.string().uuid(),
  preferredStartsAt: z
    .string()
    .datetime()
    .refine((value) => new Date(value).getTime() > Date.now() - 60_000, {
      message: "Booking time must be in the future",
    }),
  addressLine1: z.string().trim().min(5).max(150),
  addressLine2: z.string().max(150).optional(),
  city: z.string().trim().min(2).max(80),
  region: z.string().trim().min(2).max(80),
  postalCode: z.string().trim().min(3).max(20),
  country: z.string().length(2).default("US"),
  clientNotes: z.string().max(2000).optional(),
});

export const bookingStatusUpdateSchema = z.object({
  status: z.enum(bookingStatuses),
  privateNotes: z.string().max(4000).optional(),
  confirmedStartsAt: z.string().datetime().optional(),
  cancellationReason: z.string().max(500).optional(),
});

export const bookingClientCancelSchema = z.object({
  cancellationReason: z.string().trim().max(500).optional(),
});

export const bookingClientRescheduleSchema = z.object({
  preferredStartsAt: z
    .string()
    .datetime()
    .refine((value) => new Date(value).getTime() > Date.now() - 60_000, {
      message: "Booking time must be in the future",
    }),
});

export const mediaUploadUrlSchema = z.object({
  kind: z.enum(["LOGO", "HERO", "GALLERY", "SERVICE", "PROFILE", "OTHER"]),
  fileName: z.string().trim().min(1).max(180),
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(5 * 1024 * 1024),
});

export const mediaConfirmSchema = z.object({
  objectKey: z.string().trim().min(8).max(500),
  kind: z.enum(["LOGO", "HERO", "GALLERY", "SERVICE", "PROFILE", "OTHER"]),
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(5 * 1024 * 1024),
  altText: z.string().trim().max(200).optional(),
  setAsLogo: z.boolean().optional(),
});

export const websiteUpdateSchema = z.object({
  themeId: z.enum(websiteThemes).optional(),
  layoutFamily: z.enum(websiteLayoutFamilies).optional(),
  sectionOrder: z.array(z.enum(websiteSectionKeys)).optional(),
  enabledSections: z.array(z.enum(websiteSectionKeys)).optional(),
  seoTitle: z.string().max(120).optional(),
  seoDescription: z.string().max(300).optional(),
  status: z.enum(["DRAFT", "PUBLISHED", "UNPUBLISHED"]).optional(),
});

export const websiteSectionUpdateSchema = z.object({
  title: z.string().max(200).optional(),
  content: z.record(z.string(), z.unknown()),
  isEnabled: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(100).optional(),
});

/** Structured website + business app blueprint. Never executable code. */
export const aiBusinessProfileSchema = z.object({
  businessName: z.string().min(1).max(120),
  industry: z.enum(businessIndustries),
  description: z.string().max(5000).default(""),
  phoneNumber: z.string().max(24).optional(),
  email: z.string().email().optional().or(z.literal("")),
  address: z
    .object({
      addressLine1: z.string().max(150).optional(),
      city: z.string().max(80).optional(),
      region: z.string().max(80).optional(),
      postalCode: z.string().max(20).optional(),
      country: z.string().length(2).default("US"),
    })
    .default({ country: "US" }),
  serviceAreas: z.array(z.string().min(1).max(80)).max(50).default([]),
  businessHours: z
    .record(
      z.string(),
      z.object({
        open: z.string(),
        close: z.string(),
        closed: z.boolean().optional(),
      }),
    )
    .optional(),
  services: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        description: z.string().max(2000).optional(),
        pricingType: z.enum(servicePricingTypes).default("FIXED"),
        priceCents: z.number().int().min(0).optional(),
        durationMinutes: z.number().int().min(15).optional(),
      }),
    )
    .max(40)
    .default([]),
  pricingModel: z.string().max(200).optional(),
  bookingAvailability: z.string().max(500).optional(),
  brandTone: z.string().max(120).optional(),
  contentTone: z.enum(contentTones).default("professional"),
  brandDirection: z.string().max(400).optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#0F766E"),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#134E4A"),
  heroHeadline: z.string().max(200).default(""),
  callToAction: z.string().max(120).default("Book a Service"),
  aboutContent: z.string().max(8000).default(""),
  faqs: z
    .array(
      z.object({ question: z.string().max(300), answer: z.string().max(2000) }),
    )
    .max(20)
    .default([]),
  cancellationPolicy: z.string().max(4000).optional(),
  contactInformation: z
    .object({
      phone: z.string().max(24).optional(),
      email: z.string().max(120).optional(),
    })
    .optional(),
  themeId: z.enum(websiteThemes).default("CLEAN_MODERN"),
  layoutFamily: z.enum(websiteLayoutFamilies).default("CLASSIC"),
  enabledSections: z.array(z.enum(websiteSectionKeys)).optional(),
  sectionOrder: z.array(z.enum(websiteSectionKeys)).optional(),
  seoTitle: z.string().max(120).optional(),
  seoDescription: z.string().max(300).optional(),
  followUpQuestions: z.array(z.string().max(200)).max(5).default([]),
  generationSummary: z.string().max(500).optional(),
});

export type AiBusinessProfile = z.infer<typeof aiBusinessProfileSchema>;

export const aiOnboardingMessageSchema = z.object({
  conversationId: z.string().uuid().optional(),
  message: z.string().trim().min(1).max(8000),
});

export const aiOnboardingApproveSchema = z.object({
  conversationId: z.string().uuid(),
  profile: aiBusinessProfileSchema,
});

export const aiAssistantMessageSchema = z.object({
  conversationId: z.string().uuid().optional(),
  message: z.string().trim().min(1).max(4000),
  previewOnly: z.boolean().default(true),
});

export const aiAssistantApplySchema = z.object({
  conversationId: z.string().uuid(),
  messageId: z.string().uuid(),
});

export const reviewCreateSchema = z.object({
  bookingId: z.string().uuid(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().max(2000).optional(),
});

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "business";

export type BusinessOwnerRegisterInput = z.infer<
  typeof businessOwnerRegisterSchema
>;
export type BusinessClientRegisterInput = z.infer<
  typeof businessClientRegisterSchema
>;
export type BusinessUpdateInput = z.infer<typeof businessUpdateSchema>;
export type ServiceCreateInput = z.infer<typeof serviceCreateSchema>;
export type BookingCreateInput = z.infer<typeof bookingCreateSchema>;
